// src/pages/SubscriptionPage.js
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth } from "../firebase";
import "../App.css";
import { Typography, Tooltip } from "@mui/material";
import InvoiceRequestModal from "../components/InvoiceRequestModal";

/* ---------- Stripe Price IDs (recurring Prices) ---------- */
const PRICES = {
  personal: { monthly: "price_1SkTXfH9XJICOtRv6wyEOGPx", annual: "price_1SkTYgH9XJICOtRvEoFOMCTb" },
  family_lite: { monthly: "price_1SkTZcH9XJICOtRvyJbPQICt", annual: "price_1SkTaFH9XJICOtRvdEhqQHbt" },
  family: { monthly: "price_1SkTcVH9XJICOtRvbYAg8l6d", annual: "price_1SkTeqH9XJICOtRvnfbc6xV9" },
};

/* ---------- Config ---------- */
const BILLING_DISCOUNT_CONSUMER = 0.15;
// ✅ Invoice / PO schools: annual only (no monthly invoicing)
const INVOICE_PO_ANNUAL_ONLY = true;

// HTTPS endpoint for SUBSCRIPTIONS (mode: "subscription")
const SUBSCRIPTION_HTTP_FN = "https://createsubscriptioncheckoutsessionhttp-szcrkw6k7q-uc.a.run.app";

/* ---------- UI helpers ---------- */
const styles = {
  section: { margin: "64px auto", maxWidth: 1200 },
  pageTitle: { fontFamily: "Montserrat, sans-serif", color: "#1E3A8A", marginTop: 0 },
  subheading: { color: "#1E3A8A", fontFamily: "Montserrat, sans-serif" },
  cardTitle: {
    fontFamily: '"Poppins", "Montserrat", sans-serif',
    fontWeight: 800,
    fontSize: "1.5rem",
    letterSpacing: "0.2px",
    color: "#1E3A8A",
    marginBottom: 4,
  },
  priceBadge: {
    backgroundColor: "#274C77",
    color: "#fff",
    padding: "6px 10px",
    borderRadius: 999,
    display: "inline-block",
    fontWeight: 700,
    fontSize: "0.9rem",
  },
  planCard: {
    width: 280,
    minHeight: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    padding: "18px 20px 20px",
    textAlign: "left",
    borderRadius: 16,
    background: "rgba(255,255,255,0.97)",
    boxShadow: "0 10px 26px rgba(15,23,42,0.14)",
    position: "relative",
    overflow: "hidden",
  },
  cardMetaRow: {
    fontSize: "0.85rem",
    opacity: 0.8,
    marginBottom: 6,
  },
  cardFooter: {
    marginTop: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 6,
  },
};

// ✅ NEW: footer link style used in the footer
const footerLinkStyle = {
  color: "white",
  textDecoration: "none",
};

const plans = [
  {
    id: "free",
    name: "Free Plan",
    priceMonthly: 0,
    cap: 5,
    profiles: 1,
    bullets: ["Personalised stories", "Smart Dashboard & Analytics", "5 stories/month"],
  },
  {
    id: "personal",
    name: "Personal Plan",
    priceMonthly: 6.99,
    cap: 50,
    profiles: 1,
    bullets: ["All features incl. Smart Dashboard, phonics & quizzes", "50 stories/month", "Single reader profile"],
  },
  {
    id: "family_lite",
    name: "Family Lite Plan",
    priceMonthly: 9.99,
    cap: 80,
    profiles: 3,
    bullets: ["Up to 3 reader profiles", "Smart Family dashboard with Analytics", "80 stories/month"],
  },
  {
    id: "family",
    name: "Big Family Plan",
    priceMonthly: 14.99,
    cap: 150,
    profiles: 5,
    bullets: ["150 stories/month (best value)", "Up to 5 reader profiles", "Everything in Personal & Family Lite Plans"],
  },
];

const formatGBP = (n) => `£${n.toFixed(2)}`;

const AddOnsInfoBanner = () => (
  <div
    id="addons"
    role="note"
    className="subscription-title-card"
    style={{
      background: "rgba(227, 241, 255, 0.95)",
      border: "1px solid #9CC9FF",
      color: "#0B2A4A",
      padding: 20,
      borderRadius: 12,
      margin: "0 auto 24px",
      maxWidth: 900,
      textAlign: "left",
    }}
  >
    <h3 style={{ margin: 0, color: "#1E3A8A", fontFamily: "Montserrat, sans-serif" }}>Add-Ons (Extra Stories)</h3>
    <p style={{ margin: "8px 0 0" }}>
      When you’ve used your monthly stories, you can buy extra packs to keep going. Add-ons are optional and expire 12
      months after purchase.
    </p>
    <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 18, lineHeight: 1.6 }}>
      <li>
        <em>Extra 20</em> — £1.99
      </li>
      <li>
        <em>Extra 100</em> — £8.99
      </li>
    </ul>
  </div>
);

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const cancelled = new URLSearchParams(location.search).get("cancelled") === "1";
  const upgrading = new URLSearchParams(location.search).get("upgrade") === "1"; // 🔁 upgrade mode

  const [billingConsumer, setBillingConsumer] = useState(
    (localStorage.getItem("selectedBilling") || "monthly").toLowerCase() === "annual" ? "annual" : "monthly"
  );

  const [user, setUser] = useState(null);
  const [starting, setStarting] = useState(false); // starting checkout
  const [changing, setChanging] = useState(false); // opening portal
  const [status, setStatus] = useState(""); // UI hint
  const autostartRanRef = useRef(false);

  useEffect(() => {
    const off = onAuthStateChanged(auth, (u) => setUser(u));
    return () => off();
  }, []);

  // 🔹 Always start at top when visiting /subscriptions
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  // After sign-in, auto-start saved checkout once (not in upgrade mode)
  useEffect(() => {
    if (!user || starting || upgrading) return;

    const autostart = localStorage.getItem("checkoutAutostart") === "1";
    if (!autostart) return;

    // ✅ hard guard: prevents any duplicate run in the same mount lifecycle
    if (autostartRanRef.current) return;
    autostartRanRef.current = true;

    const rememberedPlan = localStorage.getItem("selectedPlan");
    const rememberedBilling = (localStorage.getItem("selectedBilling") || "monthly").toLowerCase();
    if (!rememberedPlan) return;

    const billKey = rememberedBilling === "annual" ? "annual" : "monthly";
    const priceId = PRICES[rememberedPlan]?.[billKey];
    if (!priceId) return;

    // ✅ IMPORTANT: clear flags BEFORE redirecting to Stripe
    localStorage.removeItem("checkoutAutostart");
    localStorage.removeItem("selectedPlan");
    localStorage.removeItem("selectedBilling");
    localStorage.removeItem("selectedPlanLabel");

    setStarting(true);

    startSubscriptionCheckout(priceId, 1, {
      kind: "subscription",
      plan: rememberedPlan,
      billing: rememberedBilling,
      label: localStorage.getItem("selectedPlanLabel") || "",
    }).catch((e) => {
      console.error("[checkout autostart] error:", e);
      alert(e?.message || String(e) || "Checkout failed");
      setStarting(false);
    });
  }, [user, starting, upgrading]);

  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoicePkg, setInvoicePkg] = useState(null);
  const [invoiceModalKey, setInvoiceModalKey] = useState(0);

  const openInvoiceModal = (pkg) => {
    setInvoicePkg(pkg);
    setInvoiceModalKey((k) => k + 1);
    setInvoiceOpen(true);
  };

  const submitInvoiceRequest = async (payload) => {
    // ✅ force annual-only for invoice/PO
    const billingInterval = INVOICE_PO_ANNUAL_ONLY ? "annual" : payload?.billingInterval || payload?.billing || "annual";

    // ✅ "one box" schools: always a custom package key
    const packageKey = payload?.packageKey || invoicePkg?.packageKey || "school_custom";

    // ✅ seats must come from the modal input (ask for approx student count)
    const seats = Number(payload?.seats || payload?.students || 0);

    if (!packageKey) throw new Error("Missing school package selection (packageKey).");
    if (!seats || seats <= 0) throw new Error("Please enter an approximate number of student seats.");

    const finalPayload = {
      schoolName: payload?.schoolName,
      contactName: payload?.contactName,
      email: payload?.email,
      country: payload?.country,
      phoneNumber: payload?.phoneNumber,

      packageKey,
      seats,
      billingInterval, // "annual"

      planLabel: payload?.planLabel || invoicePkg?.planLabel || "Schools – Custom pricing",
      source: "invoice",
    };

    const resp = await fetch("https://us-central1-yourstoryworld-df05d.cloudfunctions.net/requestSchoolInvoiceHttp", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(finalPayload),
    });

    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(json?.error || `Request failed (${resp.status})`);
    return json;
  };

  /* ------------------------------------------------------------------
     SUBSCRIPTIONS (new purchase): call HTTP endpoint with ID token
  ------------------------------------------------------------------ */
  const startSubscriptionCheckout = async (priceId, quantity = 1, metadata = {}) => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not signed in");

    const idToken = await currentUser.getIdToken(true);
    const payload = {
      priceId,
      quantity,
      successUrl: `${window.location.origin}/checkout/success`,
      cancelUrl: `${window.location.origin}/subscriptions?cancelled=1`,
      metadata,
    };

    const resp = await fetch(SUBSCRIPTION_HTTP_FN, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(payload),
    });

    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(json?.error?.message || `Checkout failed (${resp.status}).`);
    }
    if (!json?.url) throw new Error("No checkout URL returned.");
    window.location.href = json.url;
  };

  /* ------------------------------------------------------------------
     PLAN SELECTION — ALWAYS go to /auth first (except free)
  ------------------------------------------------------------------ */
  const goToAuthFor = (planId, label, billing) => {
    localStorage.removeItem("preSignup");
    localStorage.setItem("selectedPlan", planId);
    if (label) localStorage.setItem("selectedPlanLabel", label);
    localStorage.setItem("selectedBilling", billing || "monthly");
    localStorage.setItem("checkoutAutostart", "1");
    navigate(`/auth?plan=${encodeURIComponent(planId)}&billing=${billing || "monthly"}&reset=1`);
  };

  const handleSubscribe = (planId, label, billing) => {
    if (planId === "free") {
      goToAuthFor(planId, label, billing);
      return;
    }
    goToAuthFor(planId, label, billing);
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };


  /* ------------------------------------------------------------------
     UPGRADE/DOWNGRADE via Stripe Portal
  ------------------------------------------------------------------ */
  const openBillingPortal = async () => {
    try {
      setChanging(true);
      setStatus("Opening billing portal…");
      const functions = getFunctions(undefined, "us-central1");
      const portal = httpsCallable(functions, "createPortalSession");
      const { data } = await portal({
        returnUrl: window.location.origin + "/billing/success?changed=1",
      });
      if (!data?.url) throw new Error("No portal URL returned");
      window.location.assign(data.url);
    } catch (e1) {
      console.warn("[portal callable] failed, try HTTP fallback", e1?.message || e1);
      try {
        const u = auth.currentUser;
        if (!u) throw new Error("Not signed in");
        const token = await u.getIdToken(true);
        const resp = await fetch("https://us-central1-yourstoryworld-df05d.cloudfunctions.net/createPortalSessionHttp", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(json?.error || json?.error?.message || `HTTP ${resp.status}`);
        if (!json?.url) throw new Error("No portal URL returned");
        window.location.assign(json.url);
      } catch (e2) {
        console.error("open portal error:", e2);
        alert(e2?.message || "Couldn't open billing portal. Please try again.");
        setStatus("");
        setChanging(false);
      }
    }
  };

  return (
    <div
      className="page-container"
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "100vh",
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      <style>
        {`
          @keyframes aiGlowSubs {
            0% { box-shadow: 0 0 0 rgba(250, 250, 255, 0.0); transform: translateY(0) scale(1); }
            50% { box-shadow: 0 0 18px rgba(250, 250, 255, 0.8); transform: translateY(0) scale(1.04); }
            100% { box-shadow: 0 0 0 rgba(250, 250, 255, 0.0); transform: translateY(0) scale(1); }
          }
        `}
      </style>

      {/* Header */}
      <header className="page-header">
        <div className="logo">
          <img
            src={require("../assets/images/logo_name.PNG")}
            alt="YourStoryWorld Logo"
            style={{ height: "50px", cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        </div>
        <nav style={{ display: "flex", gap: "15px", justifyContent: "flex-end" }}>
          <button onClick={() => navigate("/help")} className="header-button">
            Help
          </button>
          <button onClick={() => navigate("/")} className="header-button">
            Home
          </button>
        </nav>
      </header>

      <main className="page-content" style={{ textAlign: "center", padding: "20px" }}>
        {cancelled && (
          <div
            style={{
              maxWidth: 900,
              margin: "0 auto 16px",
              background: "#fff3cd",
              color: "#856404",
              border: "1px solid #ffeeba",
              padding: 12,
              borderRadius: 8,
              textAlign: "center",
            }}
          >
            Checkout was cancelled. You can try again any time.
          </div>
        )}

        {starting && !upgrading && (
          <div
            style={{
              maxWidth: 900,
              margin: cancelled ? "0 auto 8px" : "0 auto 16px",
              background: "#E1F5FE",
              color: "#0B2A4A",
              border: "1px solid #81D4FA",
              padding: 12,
              borderRadius: 8,
              textAlign: "center",
              fontFamily: "Montserrat, sans-serif",
              fontSize: "0.95rem",
            }}
          >
            Starting your secure checkout… Redirecting to Stripe. Please don&apos;t close this tab.
          </div>
        )}

        {upgrading && (
          <div style={{ maxWidth: 900, margin: "0 auto 16px" }}>
            <div
              className="subscription-title-card"
              style={{
                background: "#E7F5FF",
                color: "#0B2A4A",
                border: "1px solid #9CC9FF",
                padding: 16,
                borderRadius: 12,
                textAlign: "center",
              }}
            >
              <h2 style={{ margin: 0 }}>Change or cancel your plan</h2>
              <p style={{ margin: "8px 0 12px" }}>
                Use the Stripe Billing Portal to upgrade, downgrade, update payment details, or cancel your subscription.
                Changes are prorated automatically.
              </p>
              <button
                onClick={openBillingPortal}
                className="subscription-button"
                style={{ minWidth: 220, opacity: changing ? 0.8 : 1 }}
                disabled={changing}
                title="Open Stripe Billing Portal"
              >
                {changing ? "Opening…" : "Open Billing Portal"}
              </button>
              {status ? <div style={{ marginTop: 10 }}>{status}</div> : null}
            </div>
          </div>
        )}

        {!upgrading && (
          <div
            className="subscription-title-card"
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              padding: "24px",
              borderRadius: "12px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              display: "inline-block",
              maxWidth: 1000,
            }}
          >
            <h1 style={styles.pageTitle}>Subscriptions & Add-Ons</h1>
            <p style={{ margin: 0 }}>
              Choose a monthly plan with clear <strong>stories per month</strong>. Add more any time with{" "}
              <strong> Extra 20 (£1.99)</strong> or <strong>Extra 100 (£8.99)</strong> packs (unused add-ons expire after 12
              months).
            </p>
            <p style={{ margin: "6px 0 16px" }}>
              All plans include: personalised stories, phonics-aligned reading, interactive quizzes, immersive audio,
              downloadable PDFs, background music options, voice recording & playback, family/school dashboards, and
              teacher-assigned themes with classroom insights.
            </p>
            <div role="group" style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              <button className="subscription-button" onClick={() => scrollTo("personal-section")}>
                Personal Packages
              </button>
              <button className="subscription-button" onClick={() => scrollTo("schools-section")}>
                School Packages
              </button>
              <button className="subscription-button" onClick={() => scrollTo("addons")}>
                Add-Ons
              </button>
            </div>
          </div>
        )}

        {!upgrading && (
          <section id="personal-section" style={styles.section}>
            <div
              className="subscription-title-card"
              style={{
                background: "rgba(255,255,255,0.95)",
                padding: "16px",
                borderRadius: "12px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                maxWidth: 1000,
                margin: "0 auto 16px",
                textAlign: "center",
              }}
            >
              <h2 style={{ ...styles.subheading, margin: 0 }}>Personal & Family</h2>
              <div style={{ margin: "12px auto 0", display: "flex", gap: 10, justifyContent: "center", alignItems: "center" }}>
                <span>Billing:</span>
                <div
                  role="tablist"
                  aria-label="Billing cycle"
                  style={{ display: "inline-flex", border: "1px solid #9CC9FF", borderRadius: 9999, overflow: "hidden" }}
                >
                  <button
                    role="tab"
                    aria-selected={billingConsumer === "monthly"}
                    className="header-button"
                    style={{
                      background: billingConsumer === "monthly" ? "#1E3A8A" : "#fff",
                      color: billingConsumer === "monthly" ? "#fff" : "#1E3A8A",
                      padding: "6px 12px",
                      border: 0,
                    }}
                    onClick={() => {
                      setBillingConsumer("monthly");
                      localStorage.setItem("selectedBilling", "monthly");
                    }}
                  >
                    Monthly
                  </button>
                  <button
                    role="tab"
                    aria-selected={billingConsumer === "annual"}
                    className="header-button"
                    style={{
                      background: billingConsumer === "annual" ? "#1E3A8A" : "#fff",
                      color: billingConsumer === "annual" ? "#fff" : "#1E3A8A",
                      padding: "6px 12px",
                      border: 0,
                    }}
                    onClick={() => {
                      setBillingConsumer("annual");
                      localStorage.setItem("selectedBilling", "annual");
                    }}
                  >
                    Annual (Save {Math.round(BILLING_DISCOUNT_CONSUMER * 100)}%)
                  </button>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: 32, flexWrap: "wrap" }}>
              {plans.map((p) => {
                const billing = billingConsumer;
                const isAnnual = billing === "annual";

                const isFree = p.id === "free";
                const disableForAnnualFree = isFree && isAnnual;

                const priceLabel = isAnnual
                  ? `${formatGBP(p.priceMonthly * 12 * (1 - BILLING_DISCOUNT_CONSUMER))}/year`
                  : `${formatGBP(p.priceMonthly)}/month`;
                const subnote = billing === "monthly" ? "Billed monthly" : `Billed annually • Save ${Math.round(BILLING_DISCOUNT_CONSUMER * 100)}%`;

                return (
                  <div key={p.id} className="subscription-card" style={styles.planCard}>
                    <div>
                      <h3 style={styles.cardTitle}>{p.name}</h3>

                      <div style={styles.cardMetaRow}>
                        <strong>{p.cap}</strong> stories/month • <strong>{p.profiles}</strong>{" "}
                        {p.profiles === 1 ? "profile" : "profiles"}
                      </div>

                      <ul style={{ paddingLeft: "1.1rem", margin: "8px 0 0", lineHeight: 1.6, fontSize: "0.9rem" }}>
                        {p.bullets.map((b, i) => (
                          <li key={i}>{b}</li>
                        ))}
                      </ul>
                    </div>

                    <div style={styles.cardFooter}>
                      <span className="price-badge" style={styles.priceBadge}>
                        {priceLabel}
                      </span>
                      <div style={{ fontSize: 12, opacity: 0.85 }}>{subnote}</div>
                      <button
                        onClick={() => handleSubscribe(p.id, p.name, billing)}
                        className="subscription-button"
                        style={{ marginTop: 4 }}
                        disabled={starting || disableForAnnualFree}
                        title={disableForAnnualFree ? "Free plan is only available on monthly billing" : "Select this plan"}
                      >
                        {starting ? "Starting…" : "Select"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ✅ Schools: ONE CONTACT BOX (no pricing shown) */}
        {!upgrading && (
          <section id="schools-section" style={styles.section}>
            <div
              className="subscription-title-card"
              style={{
                background: "rgba(255,255,255,0.95)",
                padding: "16px",
                borderRadius: "12px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                maxWidth: 1000,
                margin: "0 auto 16px",
                textAlign: "center",
              }}
            >
              <h2 id="schools" style={{ fontFamily: "Montserrat, sans-serif", margin: 0, color: "#1E3A8A" }}>
                Schools
              </h2>
              <p style={{ fontFamily: "Montserrat, sans-serif", margin: "6px 0 0" }}>
                We offer <strong>tailored school pricing</strong> based on pupil numbers, onboarding needs and support level.
                Get in touch and we’ll recommend the right package.
              </p>
              {INVOICE_PO_ANNUAL_ONLY && (
                <p style={{ margin: "10px 0 0", fontSize: 13, color: "#334155" }}>
                  <strong>Invoice / PO purchases are annual only.</strong>
                </p>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: 32, flexWrap: "wrap" }}>
              <div className="subscription-card" style={{ ...styles.planCard, minWidth: 280, width: 320 }}>
                <Tooltip title="Includes AI literacy lesson ideas, prompt practice & fact-checking activities for pupils." arrow>
                  <div
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 14,
                      backgroundColor: "#6D28D9",
                      color: "#F9FAFB",
                      borderRadius: 999,
                      padding: "4px 10px",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      animation: "aiGlowSubs 1.8s ease-in-out infinite",
                      cursor: "default",
                    }}
                  >
                    AI-Ready ✓
                  </div>
                </Tooltip>

                <div>
                  <h3 style={styles.cardTitle}>Custom school plan</h3>
                  <div style={styles.cardMetaRow}>
                    <strong>Any size</strong> • primary, secondary, MATs & local authorities
                  </div>

                  <p style={{ margin: "6px 0 10px" }}>
                    Pricing is tailored. We’ll ask a few quick questions and send a clear quote + onboarding plan.
                  </p>

                  <ul style={{ paddingLeft: "1.1rem", margin: "4px 0 0", lineHeight: 1.6, fontSize: "0.9rem" }}>
                    <li>Student logins + unlimited teacher accounts</li>
                    <li>Classroom insights, themes, phonics & quizzes</li>
                    <li>Optional onboarding/training for staff</li>
                  </ul>
                </div>

                <div style={styles.cardFooter}>
                  <span className="price-badge" style={styles.priceBadge}>
                    Contact us for pricing
                  </span>

                  <button
                    onClick={() => navigate("/contact?type=sales&topic=schools")}
                    className="subscription-button"
                    style={{ marginTop: 6 }}
                    disabled={starting}
                    title="Speak to us about school pricing"
                  >
                    Talk to us
                  </button>

                  <button
                    onClick={() =>
                      openInvoiceModal({
                        packageKey: "school_custom",
                        planLabel: "Schools – Custom pricing",
                        billingInterval: "annual",
                        billing: "annual",
                        source: "invoice_po",
                      })
                    }
                    disabled={starting}
                    style={{
                      marginTop: 6,
                      padding: "8px 14px",
                      borderRadius: 10,
                      backgroundColor: "#EEF2FF",
                      border: "1px solid #6366F1",
                      color: "#1E3A8A",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      cursor: "pointer",
                    }}
                  >
                    Request invoice / PO
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {!upgrading && (
          <section style={styles.section}>
            <AddOnsInfoBanner />
          </section>
        )}

        {!upgrading && (
          <section style={styles.section}>
            <div
              className="subscription-title-card"
              style={{
                background: "rgba(255,255,255,0.85)",
                padding: "16px",
                borderRadius: "12px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                maxWidth: 900,
                margin: "0 auto",
                textAlign: "center",
              }}
            >
              <h3 style={{ margin: 0, color: "#1E3A8A", fontFamily: "Montserrat, sans-serif" }}>Before you subscribe</h3>
              <p style={{ margin: "6px 0 0" }}>Below you’ll find our terms, billing notes and how add-ons work.</p>
            </div>
          </section>
        )}

        {!upgrading && (
          <section id="terms" style={{ ...styles.section, maxWidth: 900 }}>
            <div
              className="subscription-title-card"
              style={{
                background: "rgba(255,255,255,0.95)",
                padding: "24px",
                borderRadius: "12px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                textAlign: "left",
                fontFamily: '"Inter", "Montserrat", sans-serif',
              }}
            >
              <h3 style={{ marginTop: 0, color: "#1E3A8A", fontFamily: '"Inter", "Montserrat", sans-serif' }}>Terms</h3>
              <ul style={{ lineHeight: 1.7, marginTop: 8 }}>
                <li>
                  <strong>What counts as a story:</strong> each time you tap <em>Generate</em>, that’s 1 story.
                </li>
                <li>
                  <strong>Monthly reset:</strong> stories reset at 00:00 Europe/London on your renewal date.
                </li>
                <li>
                  <strong>Add-ons:</strong> you can buy <em>Extra 20 (£1.99)</em> or <em>Extra 100 (£8.99)</em> any time. Unused
                  add-ons expire after 12 months.
                </li>
                <li>
                  <strong>Plan changes:</strong> upgrades apply immediately upon confirmation; downgrades at next renewal.
                </li>
                <li>
                  <strong>Failures:</strong> if generation fails due to a platform error, it doesn’t count.
                </li>
                <li>
                  <strong>Fair use:</strong> normal household/classroom use; automated/bulk misuse may be limited.
                </li>
              </ul>
              <p style={{ opacity: 0.9, marginTop: 8 }}>
                All prices include VAT. School pricing is provided on request. Invoice / PO purchases are annual only.
              </p>
            </div>
          </section>
        )}

        <InvoiceRequestModal
          key={invoiceModalKey}
          open={invoiceOpen}
          onClose={() => {
            setInvoiceOpen(false);
            setInvoicePkg(null);
          }}
          selectedPackage={invoicePkg}
          onSubmit={submitInvoiceRequest}
        />
      </main>

      {/* Footer */}
      <footer
        style={{
          textAlign: "center",
          padding: "20px",
          backgroundColor: "#000",
          color: "#fff",
        }}
      >
        <Typography variant="body2">
          © {new Date().getFullYear()} YourStoryWorld Ltd · Company No. 17098564 · All
          rights reserved.
        </Typography>

        <Typography variant="body2" style={{ marginTop: "8px" }}>
          <a href="/about" style={footerLinkStyle}>
            About Us
          </a>{" "}
          |{" "}
          <a href="/contact" style={footerLinkStyle}>
            Contact
          </a>{" "}
          |{" "}
          <a href="/help" style={footerLinkStyle}>
            Help & FAQs
          </a>
        </Typography>

        <Typography variant="body2" style={{ marginTop: 4 }}>
          <a href="/privacy-policy" style={footerLinkStyle}>
            Privacy Policy
          </a>{" "}
          |{" "}
          <a href="/terms" style={footerLinkStyle}>
            Terms of Use
          </a>{" "}
          |{" "}
          <a href="/cookie-policy" style={footerLinkStyle}>
            Cookie Policy
          </a>{" "}
          |{" "}
          <a href="/child-safety" style={footerLinkStyle}>
            Child Safety
          </a>{" "}
          |{" "}
          <a href="/accessibility" style={footerLinkStyle}>
            Accessibility
          </a>{" "}
          |{" "}
          <a href="/data-protection" style={footerLinkStyle}>
            Data Protection &amp; Security
          </a>
        </Typography>

        <Typography variant="caption" style={{ marginTop: "10px", display: "block", color: "#ccc" }}>
          ⚠️ Disclaimer: The stories generated by YourStoryWorld are created using AI and are intended for entertainment and
          educational purposes only. While we strive for accuracy, some content may contain factual or narrative
          inconsistencies.
          <p style={{ fontSize: "0.8rem", color: "#e11d48", marginTop: "10px" }}>
            <strong>Child-Safe Content:</strong> YourStoryWorld uses AI safety filters to ensure all stories remain
            age-appropriate. Violent, explicit, discriminatory, or harmful content is automatically removed or rewritten.
            Minor variance may occur due to the nature of AI generation.
          </p>
        </Typography>
      </footer>
    </div>
  );
};

export default SubscriptionPage;